import { useState } from "react";

import { v4 as uuidv4 } from "uuid";

import { useUploadFile } from "src/api/filesApi/queries";
import { UploadFileEnvFilesPostEnvEnum } from "src/clients/files";
import { toastActions } from "src/design-system/Toast/utils";
import {
  COPILOT_FILE_SOURCE,
  MAX_COPILOT_CHAT_ATTACHMENTS,
} from "src/unifiedCopilot/chat/attachments/constants";

export type ChatAttachment =
  | { key: string; file: File; status: "uploading" }
  | { key: string; file: File; status: "uploaded"; fileId: string };

type UseChatAttachmentsReturn = {
  /** Attachments in the order they were added — the order they are sent in. */
  attachments: ChatAttachment[];
  addFile: (file: File) => void;
  removeAttachment: (key: string) => void;
  clearAttachments: () => void;
  hasPendingUploads: boolean;
  isFull: boolean;
};

/**
 * Draft attachments for the copilot chat composer. Files upload to the
 * workspace's live Files API the moment they are added — the copilot runner
 * only resolves live file ids, and uploading eagerly lets the chip show
 * upload progress before send.
 */
export const useChatAttachments = ({
  resetKey,
}: {
  /** Attachments are dropped when this changes (e.g. a new session starts). */
  resetKey: string | null;
}): UseChatAttachmentsReturn => {
  const { onFileUpload } = useUploadFile();
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);

  // Adjust-during-render instead of an effect: staged attachments belong to
  // the conversation they were staged in.
  const [previousResetKey, setPreviousResetKey] = useState(resetKey);
  if (resetKey !== previousResetKey) {
    setPreviousResetKey(resetKey);
    setAttachments([]);
  }

  const addFile = (file: File): void => {
    if (attachments.length >= MAX_COPILOT_CHAT_ATTACHMENTS) return;

    const key = uuidv4();
    // Re-checked inside the update so racing adds cannot exceed the cap; a
    // clamped entry never appears, and its resolving upload no-ops below.
    setAttachments((previous) =>
      previous.length >= MAX_COPILOT_CHAT_ATTACHMENTS
        ? previous
        : [...previous, { key, file, status: "uploading" }],
    );

    const removeWithRetryToast = (): void => {
      setAttachments((previous) =>
        previous.filter((attachment) => attachment.key !== key),
      );
      toastActions.failure({
        title: "File Upload Failed",
        description: `Upload failed for '${file.name}'. Please try again.`,
        actionText: "Try again",
        onActionClick: () => addFile(file),
      });
    };

    onFileUpload(
      file,
      `file-upload-${COPILOT_FILE_SOURCE}-${key}`,
      UploadFileEnvFilesPostEnvEnum.LIVE,
      COPILOT_FILE_SOURCE,
    )
      .then((fileId) => {
        if (fileId === undefined) {
          removeWithRetryToast();
          return;
        }
        // Mapping by key means an attachment removed mid-upload stays removed.
        setAttachments((previous) =>
          previous.map((attachment) =>
            attachment.key === key
              ? { key, file, status: "uploaded", fileId }
              : attachment,
          ),
        );
      })
      .catch(removeWithRetryToast);
  };

  const removeAttachment = (key: string): void => {
    setAttachments((previous) =>
      previous.filter((attachment) => attachment.key !== key),
    );
  };

  const clearAttachments = (): void => {
    setAttachments([]);
  };

  return {
    attachments,
    addFile,
    removeAttachment,
    clearAttachments,
    hasPendingUploads: attachments.some(
      (attachment) => attachment.status === "uploading",
    ),
    isFull: attachments.length >= MAX_COPILOT_CHAT_ATTACHMENTS,
  };
};
